import React, { useState, useEffect } from "react";

import { Loader2, Plus, Minus, CreditCard } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { useAuth } from "../../../contexts/AuthContext";
import { billingApi, PaymentMethod } from "../../../lib/api/billing";
import { useCurrencyContext } from "../../../hooks/useCurrencyContext";
import { formatUsdDollars, formatMinor, localizeUsdCents } from "../../../lib/currency";
import { useEmbedStore } from "../../../stores/embed-store";
import toast from "react-hot-toast";

interface AddFundsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface PaymentResult {
  amount: number;
  subtotal: number;
  tax: number;
  total: number;
  cardLabel: string;
}

const PRESET_AMOUNTS = [10, 25, 50, 100];

const formatBrand = (brand: string): string => {
  const brands: Record<string, string> = {
    visa: "Visa", mastercard: "Mastercard", amex: "Amex",
    discover: "Discover", diners: "Diners", jcb: "JCB", unionpay: "UnionPay",
  };
  return brands[brand] || brand.charAt(0).toUpperCase() + brand.slice(1);
};

export const AddFundsModal: React.FC<AddFundsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { getIdToken } = useAuth();
  const { context: currency, refresh: refreshCurrency } = useCurrencyContext();
  const [amount, setAmount] = useState<number>(25);
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState<PaymentMethod | null>(null);
  const [cardLoading, setCardLoading] = useState(true);
  const [result, setResult] = useState<PaymentResult | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadCard();
      setAmount(25);
      setResult(null);
    }
  }, [isOpen]);

  const loadCard = async (): Promise<void> => {
    setCardLoading(true);
    setCard(null);
    try {
      const token = await getIdToken();
      if (!token) return;
      const pmResponse = await billingApi.getPaymentMethods(token).catch(() => null);
      if (pmResponse?.paymentMethods?.length) {
        const invoiceDefault = pmResponse.paymentMethods.find(pm => pm.isInvoiceDefault);
        const subscriptionDefault = pmResponse.paymentMethods.find(pm => pm.isSubscriptionDefault);
        setCard(invoiceDefault || subscriptionDefault || pmResponse.paymentMethods[0]);
      }
    } catch {
      // Card loading is best-effort — fallback to Checkout
    } finally {
      setCardLoading(false);
    }
  };

  const handleIncrement = (): void => {
    setAmount((prev) => prev + 5);
  };

  const handleDecrement = (): void => {
    setAmount((prev) => Math.max(prev - 5, 5));
  };

  const cardLabel = card
    ? `${formatBrand(card.brand)} •••• ${card.last4}`
    : "";

  const handleSubmit = async (): Promise<void> => {
    if (amount < 5) {
      toast.error(`Minimum amount is ${formatUsdDollars(5, currency.displayCurrency, currency.rates)}`);
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      if (card) {
        const response = await billingApi.chargeCloudFunds(token, amount, card.id);
        // Subtotal localized from USD; tax/total come back already in the charged currency.
        const subtotal = localizeUsdCents(Math.round(amount * 100), currency.displayCurrency, currency.rates);
        setResult({
          amount,
          subtotal,
          tax: response.tax,
          total: response.amountCharged,
          cardLabel,
        });
        onSuccess?.();
        // This charge may have pinned the customer's Stripe currency — refresh
        // so the currency selector locks immediately (no reload needed).
        void refreshCurrency();
      } else {
        const { url } = await billingApi.addCloudFunds(token, amount);

        // In embed mode, open in new tab (Stripe can't load in iframe)
        if (useEmbedStore.getState().isEmbedded) {
          window.open(url, '_blank');
          toast.success('Checkout opened in new tab');
          onClose();
          setLoading(false);
        } else {
          window.location.href = url;
        }
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      console.error("Error processing add funds:", err);
      toast.error(axiosErr.response?.data?.message || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDone = (): void => {
    setResult(null);
    onClose();
  };

  const isPreset = PRESET_AMOUNTS.includes(amount);

  return (
    <Dialog open={isOpen} onOpenChange={result ? handleDone : onClose}>
      <DialogContent className="max-w-[90vw] w-full sm:max-w-[420px] rounded-xl p-0 gap-0" onOpenAutoFocus={(e) => e.preventDefault()}>

        {/* Success state */}
        {result ? (
          <div className="px-6 py-8">
            <div className="text-center mb-6">
              <p className="text-sm font-medium text-muted-foreground">Payment Confirmed</p>
              <p className="text-xl font-semibold mt-1">{formatUsdDollars(result.amount, currency.displayCurrency, currency.rates)} added to balance</p>
            </div>

            <div className="border-t border-b border-border py-4 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">{formatMinor(result.subtotal, currency.displayCurrency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span className="tabular-nums">{formatMinor(result.tax, currency.displayCurrency)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium">
                <span>Charged</span>
                <span className="tabular-nums">{formatMinor(result.total, currency.displayCurrency)}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4 mb-6">
              <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{result.cardLabel}</span>
            </div>

            <Button
              onClick={handleDone}
              className="w-full h-12 text-sm font-medium bg-foreground hover:bg-foreground/90 text-background"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-2">
              <DialogTitle className="text-lg">Add Funds</DialogTitle>
              <DialogDescription className="text-sm">
                Add funds to your cloud balance for usage-based services
              </DialogDescription>
            </DialogHeader>

            {cardLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="px-6 pb-6">
                {/* Preset quick-select */}
                <div className="flex gap-2 mb-4">
                  {PRESET_AMOUNTS.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setAmount(preset)}
                      disabled={loading}
                      className={`flex-1 h-9 rounded-lg text-sm font-medium transition-colors ${
                        amount === preset
                          ? "bg-foreground text-background"
                          : "bg-muted text-foreground hover:bg-muted/80"
                      } disabled:opacity-30`}
                    >
                      {formatUsdDollars(preset, currency.displayCurrency, currency.rates)}
                    </button>
                  ))}
                </div>

                {/* Amount stepper */}
                <div className="flex items-center justify-center gap-4 py-4">
                  <button
                    onClick={handleDecrement}
                    disabled={amount <= 5 || loading}
                    className="h-11 w-11 rounded-full border border-border bg-background hover:bg-muted flex items-center justify-center text-foreground/60 disabled:opacity-30 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <div className="text-center min-w-[88px]">
                    <span className="text-4xl font-bold tabular-nums">{formatUsdDollars(amount, currency.displayCurrency, currency.rates)}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isPreset ? "" : "custom amount · "}min {formatUsdDollars(5, currency.displayCurrency, currency.rates)}
                    </p>
                  </div>
                  <button
                    onClick={handleIncrement}
                    disabled={loading}
                    className="h-11 w-11 rounded-full border border-border bg-background hover:bg-muted flex items-center justify-center text-foreground/60 disabled:opacity-30 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                {/* Payment method */}
                {card && (
                  <div className="flex items-center justify-between mt-2 mb-6">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {cardLabel} · {String(card.expMonth).padStart(2, "0")}/{String(card.expYear).slice(-2)}
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        const token = await getIdToken();
                        if (!token) return;
                        try {
                          const { url } = await billingApi.createPortalSession(token);
                          window.open(url, "_blank");
                        } catch {
                          toast.error("Failed to open billing portal");
                        }
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* CTA */}
                <Button
                  onClick={handleSubmit}
                  disabled={loading || amount < 5}
                  className="w-full h-12 text-sm font-medium bg-foreground hover:bg-foreground/90 text-background"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Pay ${formatUsdDollars(amount, currency.displayCurrency, currency.rates)} + tax`
                  )}
                </Button>

                {/* Fine print */}
                <p className="text-[11px] text-muted-foreground/70 text-center mt-3">
                  Funds never expire · Carry over between billing cycles
                </p>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
